/**
 * Utility to split form data into credentials and settings based on
 * the is_credential flag in field schemas.
 *
 * A field is treated as a credential if:
 * 1. is_credential === true, OR
 * 2. type === 'password' (fallback for schemas without is_credential marking)
 *
 * OAuth button fields (type === 'oauth_button') are handled specially:
 * their value is a nested credentials dict from the OAuth popup completion,
 * which gets spread into the credentials bucket as flat top-level keys.
 *
 * Keys that don't match any schema field are dropped (#184) — they're
 * invisible to the user and would otherwise leak into settings, which
 * is how OAuth-completion fields ended up clobbering meta_ads extract
 * config on modal save.
 *
 * This replaces the hardcoded per-connector extractCredentials logic.
 */

import type { ConnectorFieldSchema } from '../../types/connector-schema';

export interface SplitFormDataResult {
  credentials: Record<string, unknown>;
  settings: Record<string, unknown>;
}

/**
 * Split form values into credentials and settings based on field schemas.
 * Skips undefined, null, and empty string values.
 * Drops keys that don't correspond to any field in the schema.
 */
export function splitFormData(
  values: Record<string, unknown>,
  fields: ConnectorFieldSchema[]
): SplitFormDataResult {
  const credentials: Record<string, unknown> = {};
  const settings: Record<string, unknown> = {};

  // Build a lookup for which fields are credentials
  const credentialFields = new Set(
    fields
      .filter((f) => f.is_credential || f.type === 'password')
      .map((f) => f.name)
  );

  // Build a lookup for oauth_button fields whose values should be
  // spread into credentials as flat top-level keys
  const oauthButtonFields = new Set(
    fields
      .filter((f) => f.type === 'oauth_button')
      .map((f) => f.name)
  );

  // Schema field name set — used to drop keys that don't correspond to
  // any declared field, so OAuth-completion fields surviving in the
  // GET /{type}/{name} `connection` response (scopes, token_expires_in,
  // client_id, auth_type, obtained_via, obtained_at) can't leak into
  // the settings JSONB on PATCH. See #184.
  const schemaFieldNames = new Set(fields.map((f) => f.name));

  for (const [key, value] of Object.entries(values)) {
    // Skip empty values
    if (value === undefined || value === null || value === '') {
      continue;
    }

    // Drop keys that don't correspond to any field in the schema —
    // if a field isn't declared, the user can't see or edit it, so it
    // doesn't belong in either bucket. (#184)
    if (!schemaFieldNames.has(key)) {
      continue;
    }

    // OAuth button fields: spread the nested credentials dict into credentials
    if (oauthButtonFields.has(key)) {
      if (typeof value === 'object' && !Array.isArray(value)) {
        Object.assign(credentials, value);
      }
      continue;
    }

    if (credentialFields.has(key)) {
      credentials[key] = value;
    } else {
      settings[key] = value;
    }
  }

  return { credentials, settings };
}
